import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Plus, Trash, ArrowsOutCardinal, TextT, Calendar, X, Paperclip,
} from "@phosphor-icons/react";

/**
 * Iter208.68 — Editor Admin "Azioni per il Pro".
 *
 * Ogni azione = tasto custom visibile nel modal pratica lato Pro:
 *   - `name`               → etichetta del tasto
 *   - `visible_on_step`    → "all" | <step_id>
 *   - `blocks`             → lista di blocchi form (textarea, calendar_slots)
 *   - `confirmation_text`  → messaggio post-submit
 *   - `extra_emails`       → destinatari fissi in aggiunta all'operatore
 *
 * Progettato in modo estensibile: aggiungere un nuovo `type` di blocco =
 * aggiungere una `render` case + un preset in `BLOCK_TYPES`.
 */
const BLOCK_TYPES = [
  { key: "textarea", label: "Casella testo libero", icon: TextT },
  { key: "calendar_slots", label: "Calendario disponibilità", icon: Calendar },
  { key: "file_upload", label: "Allega file", icon: Paperclip },
];

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export default function CustomActionsEditor({ actions = [], steps = [], onChange }) {
  const set = (i, patch) => {
    const next = [...actions];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const addAction = () => {
    onChange([
      ...actions,
      {
        id: uid(),
        name: "",
        visible_on_step: "all",
        confirmation_text: "Richiesta inviata! Ti contatteremo a breve.",
        extra_emails: [],
        blocks: [],
      },
    ]);
  };
  const removeAction = (i) => {
    const next = [...actions];
    next.splice(i, 1);
    onChange(next);
  };
  const moveAction = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= actions.length) return;
    const next = [...actions];
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
    onChange(next);
  };

  return (
    <div className="border rounded-md p-3 space-y-3" data-testid="custom-actions-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">🔘 Azioni per il Pro</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Tasti custom visibili nel dettaglio pratica del Pro. Ogni tasto apre un form
            configurabile e invia una email all'operatore assegnato allo step.
          </p>
        </div>
        <Button type="button" size="sm" onClick={addAction} data-testid="custom-action-add-btn" className="bg-purple-600 hover:bg-purple-700 text-white">
          <Plus size={14} weight="bold" className="mr-1" /> Aggiungi azione
        </Button>
      </div>

      {actions.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center border border-dashed rounded">
          Nessuna azione configurata.
        </div>
      ) : (
        <div className="space-y-3">
          {actions.map((a, i) => (
            <ActionCard
              key={a.id}
              action={a}
              steps={steps}
              onChange={(patch) => set(i, patch)}
              onRemove={() => removeAction(i)}
              onMoveUp={() => moveAction(i, -1)}
              onMoveDown={() => moveAction(i, +1)}
              index={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionCard({ action, steps, onChange, onRemove, onMoveUp, onMoveDown, index }) {
  const setBlock = (idx, patch) => {
    const next = [...(action.blocks || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange({ blocks: next });
  };
  const addBlock = (type) => {
    const base = { id: uid(), type, label: "", required: false };
    if (type === "calendar_slots") base.slots = [];
    if (type === "file_upload") base.max_files = 5;
    onChange({ blocks: [...(action.blocks || []), base] });
  };
  const removeBlock = (idx) => {
    const next = [...(action.blocks || [])];
    next.splice(idx, 1);
    onChange({ blocks: next });
  };
  const setEmail = (idx, val) => {
    const next = [...(action.extra_emails || [])];
    next[idx] = val;
    onChange({ extra_emails: next });
  };
  const addEmail = () => onChange({ extra_emails: [...(action.extra_emails || []), ""] });
  const removeEmail = (idx) => {
    const next = [...(action.extra_emails || [])];
    next.splice(idx, 1);
    onChange({ extra_emails: next });
  };

  return (
    <div className="border rounded-md p-3 space-y-3 bg-slate-50/60" data-testid={`custom-action-card-${index}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Nome tasto *</Label>
              <Input
                type="text"
                placeholder="Es. Richiedi documenti"
                value={action.name || ""}
                onChange={(e) => onChange({ name: e.target.value })}
                data-testid={`custom-action-name-${index}`}
              />
            </div>
            <div>
              <Label className="text-[11px]">Visibile su step</Label>
              <select
                className="w-full border rounded h-9 px-2 text-sm bg-white"
                value={action.visible_on_step || "all"}
                onChange={(e) => onChange({ visible_on_step: e.target.value })}
                data-testid={`custom-action-step-${index}`}
              >
                <option value="all">Tutti gli step</option>
                {(steps || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name || s.id}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 pt-4">
          <button
            type="button"
            className="w-7 h-7 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-200"
            onClick={onMoveUp}
            title="Sposta su"
            data-testid={`custom-action-up-${index}`}
          >
            <ArrowsOutCardinal size={14} weight="bold" />
          </button>
          <button
            type="button"
            className="w-7 h-7 inline-flex items-center justify-center rounded text-red-500 hover:bg-red-50"
            onClick={onRemove}
            title="Elimina azione"
            data-testid={`custom-action-remove-${index}`}
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Blocchi form */}
      <div className="border rounded-md p-2 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-700">Contenuto del form</div>
          <div className="flex items-center gap-1">
            {BLOCK_TYPES.map((bt) => {
              const Icon = bt.icon;
              return (
                <button
                  key={bt.key}
                  type="button"
                  onClick={() => addBlock(bt.key)}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-purple-400 hover:bg-purple-50 text-slate-700"
                  data-testid={`custom-action-add-block-${bt.key}-${index}`}
                >
                  <Icon size={12} weight="bold" />
                  {bt.label}
                </button>
              );
            })}
          </div>
        </div>
        {(action.blocks || []).length === 0 && (
          <div className="text-xs text-muted-foreground py-3 text-center border border-dashed rounded">
            Nessun blocco. Aggiungine uno dai bottoni qui sopra.
          </div>
        )}
        <div className="space-y-2">
          {(action.blocks || []).map((b, idx) => (
            <BlockEditor
              key={b.id}
              block={b}
              onChange={(patch) => setBlock(idx, patch)}
              onRemove={() => removeBlock(idx)}
              testIdPrefix={`custom-action-block-${index}-${idx}`}
            />
          ))}
        </div>
      </div>

      {/* Extra emails + confirmation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Email destinatario aggiuntivi</Label>
          <div className="space-y-1">
            {(action.extra_emails || []).map((em, idx) => (
              <div key={idx} className="flex items-center gap-1" data-testid={`custom-action-email-${index}-${idx}`}>
                <Input
                  type="email"
                  placeholder="email@dominio.it"
                  value={em}
                  onChange={(e) => setEmail(idx, e.target.value)}
                  className="text-sm"
                />
                <button type="button" onClick={() => removeEmail(idx)} className="text-red-500 hover:bg-red-50 w-8 h-8 rounded inline-flex items-center justify-center" aria-label="Rimuovi email">
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addEmail}
              className="text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1"
              data-testid={`custom-action-add-email-${index}`}
            >
              <Plus size={11} weight="bold" /> aggiungi email
            </button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1 italic">
            L'operatore assegnato allo step corrente è sempre in copia automaticamente.
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Testo di conferma finale</Label>
          <Textarea
            rows={3}
            placeholder="Es. Richiesta inviata! Il tecnico ti contatterà entro 24h."
            value={action.confirmation_text || ""}
            onChange={(e) => onChange({ confirmation_text: e.target.value })}
            data-testid={`custom-action-confirmation-${index}`}
          />
        </div>
      </div>
    </div>
  );
}

function BlockEditor({ block, onChange, onRemove, testIdPrefix }) {
  return (
    <div className="border rounded p-2 bg-slate-50/40 space-y-2" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-purple-100 text-purple-800 border border-purple-200">
          {block.type === "textarea" ? "TESTO" : block.type === "calendar_slots" ? "CALENDARIO" : block.type === "file_upload" ? "ALLEGATO" : "BLOCCO"}
        </span>
        <Input
          type="text"
          placeholder="Etichetta del blocco (es. Note per il tecnico)"
          value={block.label || ""}
          onChange={(e) => onChange({ label: e.target.value })}
          className="flex-1 text-sm"
          data-testid={`${testIdPrefix}-label`}
        />
        <label className="text-[11px] text-slate-600 inline-flex items-center gap-1 shrink-0">
          <input
            type="checkbox"
            checked={!!block.required}
            onChange={(e) => onChange({ required: e.target.checked })}
            data-testid={`${testIdPrefix}-required`}
          />
          obbligatorio
        </label>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:bg-red-50 w-7 h-7 rounded inline-flex items-center justify-center shrink-0"
          title="Rimuovi blocco"
          data-testid={`${testIdPrefix}-remove`}
        >
          <Trash size={12} weight="bold" />
        </button>
      </div>
      {block.type === "calendar_slots" && (
        <SlotsEditor
          slots={block.slots || []}
          onChange={(next) => onChange({ slots: next })}
          testIdPrefix={`${testIdPrefix}-slots`}
        />
      )}
      {block.type === "file_upload" && (
        <div className="border-l-2 border-purple-200 pl-3 flex items-center gap-2" data-testid={`${testIdPrefix}-fileopts`}>
          <label className="text-[11px] font-semibold text-slate-700">Max file:</label>
          <Input
            type="number"
            min={1}
            max={20}
            value={block.max_files ?? 5}
            onChange={(e) => onChange({ max_files: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })}
            className="w-20 text-xs h-8"
            data-testid={`${testIdPrefix}-max-files`}
          />
          <span className="text-[11px] text-slate-500 italic">(max 20 MB per file)</span>
        </div>
      )}
    </div>
  );
}

function SlotsEditor({ slots, onChange, testIdPrefix }) {
  const set = (i, patch) => {
    const n = [...slots];
    n[i] = { ...n[i], ...patch };
    onChange(n);
  };
  const add = () => onChange([...slots, { date: "", start: "10:00", end: "11:00" }]);
  const remove = (i) => {
    const n = [...slots];
    n.splice(i, 1);
    onChange(n);
  };
  return (
    <div className="border-l-2 border-purple-200 pl-3">
      <div className="text-[11px] font-semibold text-slate-700 mb-1">Slot disponibili</div>
      {slots.length === 0 && (
        <div className="text-[11px] text-slate-500 italic mb-1">Nessuno slot. Aggiungine uno qui sotto.</div>
      )}
      <div className="space-y-1">
        {slots.map((s, i) => (
          <div key={i} className="flex items-center gap-1" data-testid={`${testIdPrefix}-${i}`}>
            <Input
              type="date"
              value={s.date || ""}
              onChange={(e) => set(i, { date: e.target.value })}
              className="text-xs h-8 max-w-[140px]"
              data-testid={`${testIdPrefix}-${i}-date`}
            />
            <Input
              type="time"
              value={s.start || ""}
              onChange={(e) => set(i, { start: e.target.value })}
              className="text-xs h-8 max-w-[100px]"
              data-testid={`${testIdPrefix}-${i}-start`}
            />
            <span className="text-xs text-slate-500">→</span>
            <Input
              type="time"
              value={s.end || ""}
              onChange={(e) => set(i, { end: e.target.value })}
              className="text-xs h-8 max-w-[100px]"
              data-testid={`${testIdPrefix}-${i}-end`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-red-500 hover:bg-red-50 w-7 h-7 rounded inline-flex items-center justify-center"
              data-testid={`${testIdPrefix}-${i}-remove`}
            >
              <X size={11} weight="bold" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1 mt-1"
        data-testid={`${testIdPrefix}-add`}
      >
        <Plus size={11} weight="bold" /> aggiungi slot
      </button>
    </div>
  );
}
